import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  Renderer,
  forwardRef,
  Inject,
  ViewChild
} from '@angular/core';
import {
  DatePipe
} from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
  MYDate,
  MYOptions,
  MYDateModel
} from './interfaces/index';
import {
  PageScrollService,
  PageScrollInstance
} from 'ng2-page-scroll';
import { DOCUMENT } from '@angular/platform-browser';
/**
 * Enums
 */
enum Year {
  min = 1100,
  max = 9100
}
/**
 * Consts
 */
export const MYDP_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MonthYearPickerComponent),
  multi: true
};
/**
 * `MonthYearPickerComponent` implements a month and year picker
 */
@Component({
  selector: 'wp-month-year-picker',
  template: require('./month-year-picker.component.html'),
  styles: [require('./month-year-picker.component.scss')],
  providers: [MYDP_VALUE_ACCESSOR]
})
export class MonthYearPickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  /**
   * Month year picker options
   */
  @Input() options: MYOptions;
  /**
   * Selected date
   */
  @Input() selectedDate: MYDateModel = null;
  /**
   * Selected date change event
   */
  @Output() selectedDateChange: EventEmitter<MYDateModel>;
  /**
   * MonthPicker child
   */
  @ViewChild('MonthYearPickerTable')
  scrollContainer: any;

  // Public
  /**
   * Months
   */
  public months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  /**
   * Years
   */
  public years: number[] = [];
  /**
   * Picker visivility
   */
  public visible = false;
  /**
   * Store if is disabled or not
   */
  public isDisabled = false;

  // Private
  /**
   * Default options
   */
  private opts: MYOptions = {
    minYear: 1990,
    maxYear: <number> this.getCurrentDate().getFullYear() + 5,
    disableUntil: <MYDate> {year: 2017, month: 1},
    disableSince: <MYDate> {year: this.getCurrentDate().getFullYear() + 5, month: 12},
    dateFormat: <string> 'y-MM',
    dateViewFormat: <string> 'MM y',
    dateViewPipe: null,
    nextAndPrevButtonEnabled: <boolean> false
  };
  /**
   * Global listener
   */
  private listener: any;
  /**
   * Propagate change function
   */
  private propagateChange: (_: any) => void = () => {};

  /**
   * Creates a new `MonthYearPickerComponent` instance.
   */
  constructor(private elementRef: ElementRef,
              private renderer: Renderer,
              private datePipe: DatePipe,
              private pageScrollService: PageScrollService,
              @Inject(DOCUMENT) private document: any) {
    // Create event emmiter
    this.selectedDateChange = new EventEmitter<MYDateModel>();
    // Listen document events
    this.listener = this.renderer.listenGlobal('document', 'click', (event: any) => this.handleClick(event));
  }

  /**
   * Implements on init method.
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  ngOnInit() {
    // Set component options
    this.setOptions();
    // Generate array of years
    this.generateYears();
  }

  /**
   * Implements on destroy method
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  ngOnDestroy() {
    // If there is a globa listener, call listening
    if (this.listener) {
      this.listener();
    }
  }

  /**
   * Implements toggle for default scroll and set visivility
   */
  toggle(): void {
    // Exit if is disabled
    if (this.isDisabled) {
      return;
    }
    // Set picker visivility
    this.visible = !this.visible;
    // Default scroll
    this.selectedDate ? this.scrollTo(this.selectedDate.date.year) : this.scrollTo(null);
  }
  /**
   * Select next month
   */
  nextMonth() {
    if (this.isNextMonthDisabled()) {
      return;
    }
    let date = this.selectedDate.jsdate;
    date.setMonth(date.getMonth());

    let myDate = {
      year: date.getFullYear(),
      month: date.getMonth() + 2
    };
    this.selectDate(myDate);
  }

 /**
  * Select prev month
  */
  prevMonth() {
    if (this.isPrevMonthDisabled()) {
      return;
    }
    let date = this.selectedDate.jsdate;
    date.setMonth(date.getMonth());

    let myDate = {
      year: date.getFullYear(),
      month: date.getMonth() 
    };
    this.selectDate(myDate);
  }

  /**
   * Select cell
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {[number]} month selected month
   * @param  {[number]} year  selected year
   */
  selectCell(month: number, year: number): void {
    let date: MYDate = { month: month, year: year };
    this.selectDate(date);
    // Hide date picker
    this.visible = false;
  }

  /**
   * Implements writeValue for ControlValueAccessor interface, this takes the value from model and updates it on the view
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {Date} newDate Given date
   */
  writeValue(newDate: MYDateModel): void {
    if (newDate && newDate.jsdate) {
      let date: MYDate = { month: newDate.jsdate.getMonth() + 1, year: newDate.jsdate.getFullYear() };
      this.selectDate(date);
    }
  }

  /**
   * Implements setDisabledState for ControlValueAccessor interface
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @param  {boolean} isDisabled True if is disabled
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Implements registerOnChange for ControlValueAccessor interface, this updates our propagation function
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {Function} fn [description]
   */
  registerOnChange(fn): void {
    this.propagateChange = fn;
  }

  /**
   * Implements registerOnTouched for ControlValueAccessor interface, this updates our propagation function
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {Function} fn [description]
   */
  registerOnTouched() {}

  /**
   * Returns if a month is selected for a given year
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {number} month Month number
   * @param  {number} year  Year number
   * @return {boolean}
   */
  isCellSelected(month: number, year: number): boolean {
    return this.selectedDate &&
           this.selectedDate.date.month === month &&
           this.selectedDate.date.year === year;
  }

  /**
   * Returns if a month is disabled for a given year
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {number}  month
   * @param  {number}  year
   * @return {boolean}
   */
  isCellDisabled(month: number, year: number): boolean {
    return (this.isDisabledUntil(month, year) || this.isDisabledSince(month, year));
  }

  /**
   * Handles user click event
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {any} event Any event which ocurred
   */
  handleClick(event: any): void {
    // Get target element
    let clickedComponent = event.target;
    // Asume we are outside until we get the opposite
    let inside = false;
    // Navigating between its parent nodes checking is one of them is my component
    do {
      // If one of them is my component, I'm inside otherwise outside
      if (clickedComponent === this.elementRef.nativeElement) {
        inside = true;
      }
      clickedComponent = clickedComponent.parentNode;
    } while (clickedComponent);
    if (!inside) {
      // Outside
      // The month year picke must be hidden
      this.visible = false;
    }
  }

  /**
   * On key down event
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {KeyboardEvent} event
   */
  onKeyDown(event: KeyboardEvent): void {
    // Prevent default behaviour for all the keys
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Disable next button
   */
  isNextMonthDisabled(): boolean {
    let  month = this.selectedDate.date.month + 1;
    let  year = this.selectedDate.date.year;
    return this.isDisabled || this.isCellDisabled(month, year);
  }

  /**
   * Disable back button
   */
  isPrevMonthDisabled(): boolean {
    let  month = this.selectedDate.date.month - 1;
    let  year = this.selectedDate.date.year;
    return this.isDisabled || this.isCellDisabled(month, year);
  }

  // Private
  private selectDate(date: MYDate): void {
    // If it is disabled we cannot selected
    if (this.isCellDisabled(date.month, date.year)) {
      return;
    }
    this.selectedDate = this.getDateModel(date);
    // Emit for 2-way binding
    this.selectedDateChange.emit(this.selectedDate);
    // Propagate change for form control
    setTimeout(() => {
      this.propagateChange(this.selectedDate);
    });
  }

  /**
   * Set options for the Month Year picker
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private setOptions(): void {
    // For each user options copy on the interanl opts object
    if (this.options !== undefined) {
      Object.keys(this.options).forEach((k) => {
          (<MYOptions>this.opts)[k] = this.options[k];
      });
    }
    // If the min year exceeds min year
    if (this.opts.minYear < Year.min) {
        this.opts.minYear = Year.min;
    }
    // If the min year exceeds max year
    if (this.opts.maxYear > Year.max) {
        this.opts.maxYear = Year.max;
    }
  }

  /**
   * Get current date
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private getCurrentDate(): Date {
    return new Date();
  }

  /**
   * Generate array of years
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private generateYears(): void {
    if (this.opts.inverse) {
      // Generate years
      for (let i = this.opts.maxYear; i >= this.opts.minYear; i--) {
        this.years.push(i);
      }
    } else {
      // Generate years
      for (let i = this.opts.minYear; i <= this.opts.maxYear; i++) {
        this.years.push(i);
      }
    }
  }

  /**
   * Returns if the month for a given year is disabled until given date
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {number}  month
   * @param  {number}  year
   * @return {boolean}
   */
  private isDisabledUntil(month: number, year: number): boolean {
    return this.opts.disableUntil.month > 0 && this.opts.disableUntil.year > 0 &&
           ((year < this.opts.disableUntil.year) ||
           ((month < this.opts.disableUntil.month) && ( year === this.opts.disableUntil.year)));
  }

  /**
   * Returns if the month for a given year is disabled since given date
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {number}  month
   * @param  {number}  year
   * @return {boolean}
   */
  private isDisabledSince(month: number, year: number): boolean {
    return this.opts.disableSince.month > 0 && this.opts.disableSince.year > 0 &&
           ((year > this.opts.disableSince.year) ||
           ((month > this.opts.disableSince.month) && ( year === this.opts.disableSince.year)));
  }

  /**
   * Get date model
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {MYDate}    MYDate Current selected date
   * @return {MYDateModel}      dateModel
   */
  private getDateModel(date: MYDate): MYDateModel {
    let jsdate = new Date(date.year, date.month - 1, 1);
    let formatDate = this.formatDate(jsdate);
    return {
      date: date,
      jsdate: jsdate,
      formatted: formatDate,
      viewFormatted: this.formatViewDate(jsdate),
      epoc: Math.round(jsdate.getTime() / 1000.0)
    };
  }

  /**
   * Format dateModel
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   * @param  {Date}   date selectedDate
   * @return {string}      formatedDate
   */
  private formatDate(date: Date): string {
    if (!date) {
      return '';
    }
    return this.datePipe.transform(date, this.opts.dateFormat);
  }

  /**
   * Default scroll
   * @param year Year to scroll
   */
  private scrollTo(year: number) {
    // Check if there is year to scroll
    if (!year) {
      return ;
    }
    // Speed scroll
    const scrollSpeed = 50000;
    // Wait time
    const waitTime = 50;
    let self = this;

    setTimeout(function() {
      // A specification of the DOM element to scroll to
      let scrollTarget = '#month-year-picker-body-' + year;
      let options = {
        // The document object of the current app
        document: self.document,
        // A specification of the DOM element to scroll to
        scrollTarget: scrollTarget,
        // Array of HTMLElements or the body object that should be manipulated while performing the scroll animation
        scrollingViews: [self.scrollContainer.nativeElement],
        // Maximum speed to be used for the scroll animation
        pageScrollSpeed: scrollSpeed
      };

      // Scrolling action
      let pageScrollInstance: PageScrollInstance = PageScrollInstance.newInstance(options);
      self.pageScrollService.start(pageScrollInstance);

    }, waitTime);
  }

  /**
   * View date
   * @param  {Date}   date selectedDate
   * @return {string}      formatedDate
   */
  private formatViewDate(date: Date): string {
    if (!date) {
      return '';
    }
    if (this.opts.dateViewPipe) {
      return this.opts.dateViewPipe.transform(date);
    }
    return this.datePipe.transform(date, this.opts.dateViewFormat);
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/woopa-ui/month-year-picker/month-year-picker.component.ts